<template>
  <div class="sconf">
    <div class="sconf__main">
      <!-- ============ 판매업체 정보 ============ -->
      <section class="sui-card" data-tour="seller-settings-profile">
        <div class="sui-card__head">
          <h2 class="sui-card__title">판매업체 정보</h2>
          <span class="sui-badge" :class="isApproved ? 'sui-badge--green' : 'sui-badge--amber'">
            <AppIcon :icon="isApproved ? 'mdi-shield-check' : 'mdi-clock-outline'" :size="13" />
            {{ statusLabel }}
          </span>
        </div>
        <div class="sui-card__body sconf__body">
          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-store">업체명</label>
              <input id="c-store" v-model="form.storeName" class="sui-input" />
              <p class="sui-hint">함우리몰 상품 목록과 주문서에 노출되는 이름입니다.</p>
            </div>
            <div class="sui-field">
              <label for="c-owner">대표자명</label>
              <input id="c-owner" v-model="form.ownerName" class="sui-input" />
            </div>
          </div>

          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-biz">상호</label>
              <input id="c-biz" v-model="form.bizName" class="sui-input" />
            </div>
            <div class="sui-field">
              <label for="c-bizno">사업자등록번호</label>
              <input id="c-bizno" :value="form.bizNumber" class="sui-input" readonly />
              <p class="sui-hint">변경하려면 운영팀에 서류를 제출해야 합니다.</p>
            </div>
          </div>

          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-phone">대표 연락처</label>
              <input id="c-phone" v-model="form.phone" class="sui-input" />
            </div>
            <div class="sui-field">
              <label for="c-email">담당자 이메일</label>
              <input id="c-email" v-model="form.email" type="email" class="sui-input" />
            </div>
          </div>

          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-manager">담당자명</label>
              <input id="c-manager" v-model="form.managerName" class="sui-input" />
            </div>
            <div class="sui-field">
              <label for="c-zip">우편번호</label>
              <div class="sconf__inline">
                <input id="c-zip" :value="form.zipcode" class="sui-input" readonly @click="onSearchAddress" />
                <button type="button" class="sui-btn" :disabled="postcodeOpening" @click="onSearchAddress">
                  주소 검색
                </button>
              </div>
            </div>
          </div>

          <div class="sui-field">
            <label for="c-addr">출고지 주소</label>
            <input
              id="c-addr"
              :value="form.address"
              class="sui-input"
              placeholder="주소 검색을 눌러 주세요"
              readonly
              @click="onSearchAddress"
            />
          </div>

          <div class="sui-field">
            <label for="c-addr2">상세 주소</label>
            <input id="c-addr2" v-model="form.addressDetail" class="sui-input" />
          </div>
        </div>
      </section>

      <!-- ============ 정산 계좌 ============ -->
      <section class="sui-card" data-tour="seller-settings-bank">
        <div class="sui-card__head">
          <h2 class="sui-card__title">정산 계좌</h2>
        </div>
        <div class="sui-card__body sconf__body">
          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-bank">은행</label>
              <select id="c-bank" v-model="form.settleBank" class="sui-select sconf__select">
                <option value="">선택해 주세요</option>
                <option v-for="bank in BANKS" :key="bank" :value="bank">{{ bank }}</option>
              </select>
            </div>
            <div class="sui-field">
              <label for="c-acc">계좌번호</label>
              <input id="c-acc" v-model="form.settleAccount" class="sui-input" />
            </div>
          </div>

          <div class="sui-field">
            <label for="c-holder">예금주</label>
            <input id="c-holder" v-model="form.settleHolder" class="sui-input" />
          </div>
          <p class="sconf__notice">
            <AppIcon icon="mdi-information-outline" :size="16" />
            예금주는 사업자등록증상 상호와 일치해야 합니다. 계좌를 바꾸면 다음 정산부터 반영됩니다.
          </p>
        </div>
      </section>

      <!-- ============ 배송 · 반품 정책 ============ -->
      <section class="sui-card" data-tour="seller-settings-shipping">
        <div class="sui-card__head">
          <h2 class="sui-card__title">배송 · 반품 정책</h2>
        </div>
        <div class="sui-card__body sconf__body">
          <p class="sconf__hint">
            고객에게는 출고 배송비를 따로 받지 않습니다. 배송비는 판매가격에 포함됩니다.
            환불 배송비는 상품 등록에서 상품마다 정합니다. 기준 수량마다 한 번 받고, 수량이 넘치면 올림합니다.
            교환안내는 스토어 공통으로 상품상세에 표시됩니다. 당일 출고 문구는 관리자
            「화면 멘트」에서 정하며, 비우면 안내가 숨겨집니다.
          </p>
          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-fee">신규 상품 기본 배송비 (원)</label>
              <input id="c-fee" v-model.number="form.shippingFee" type="number" min="0" class="sui-input" />
              <p class="sui-hint">0원이면 신규 상품이 무료 배송으로 시작합니다. 기존 상품은 상품 수정에서 바꾸세요.</p>
            </div>
          </div>

          <div class="sui-row sconf__row">
            <div class="sui-field">
              <label for="c-cutoff">당일 출고 마감</label>
              <select id="c-cutoff" v-model="form.cutoff" class="sui-select sconf__select">
                <option value="12:00">12시</option>
                <option value="14:00">14시</option>
                <option value="16:00">16시</option>
              </select>
            </div>
          </div>

          <div class="sui-field">
            <label for="c-policy">교환 · 반품 안내</label>
            <textarea id="c-policy" v-model="form.returnPolicy" class="sui-input sconf__textarea" rows="4" />
          </div>
        </div>
      </section>

      <!-- ============ 알림 ============ -->
      <section class="sui-card" data-tour="seller-settings-notify">
        <div class="sui-card__head">
          <h2 class="sui-card__title">알림 받기</h2>
        </div>
        <div class="sui-card__body">
          <p class="sconf__hint">
            메일은 바로 손봐야 하는 일만 보냅니다. 신규 주문·취소/반품은 아래 스위치를 켠 경우
            등록 이메일·휴대폰으로 가고, 가입 승인·반려·정지와 주문 취소는 스위치와 관계없이
            이메일로 안내합니다. 재고·문의·정산은 판매관리센터에서 확인합니다.
          </p>
          <ul class="sconf__toggles">
            <li v-for="item in notifications" :key="item.key">
              <span>
                <strong>{{ item.label }}</strong>
                <em>{{ item.desc }}</em>
              </span>
              <label class="sconf__switch">
                <input type="checkbox" v-model="form.notify[item.key]" />
                <span />
              </label>
            </li>
          </ul>
        </div>
      </section>

      <!-- ============ 계약 · 약관 ============ -->
      <section class="sui-card" data-tour="seller-settings-contract">
        <div class="sui-card__head">
          <h2 class="sui-card__title">계약 · 약관</h2>
        </div>
        <div class="sui-card__body sconf__body">
          <p class="sconf__hint">
            공급계약서와 이용약관 본문입니다. 서명 전이라도 활성 계약서를 확인할 수 있고, 온보딩
            후에는 체결 버전과 서명이 함께 표시됩니다.
          </p>
          <p v-if="acceptsLoading" class="sconf__legal-empty">불러오는 중…</p>
          <p v-else-if="acceptsError" class="sconf__legal-empty">{{ acceptsError }}</p>
          <p v-else-if="!legalViews.length" class="sconf__legal-empty">
            등록된 계약서·약관이 없습니다. 관리자에게 문의해 주세요.
          </p>
          <template v-else>
            <ContractDocumentView
              v-for="doc in contractViews"
              :key="`${doc.code}-${doc.version}-${doc.idx ?? 'active'}`"
              mode="view"
              :title="doc.title || doc.code"
              :version="doc.version"
              :body="doc.body || ''"
              :signature="doc.signature || ''"
              :signed-at="doc.signedAt || ''"
              :party="{
                storeName: form.storeName || form.bizName,
                ownerName: form.ownerName,
                bizNumber: form.bizNumber,
                address: form.address,
                addressDetail: form.addressDetail,
              }"
              :download-stored-pdf="
                doc.hasPdf && doc.idx
                  ? () =>
                      downloadMyAcceptancePdf(
                        doc.idx as number,
                        `${doc.title || doc.code}_v${doc.version}.pdf`,
                      )
                  : undefined
              "
            />
            <p
              v-if="contractViews.length && !contractViews.some((d) => d.signature || d.signedAt)"
              class="sconf__legal-note"
            >
              아직 서명이 없습니다. 계약서 본문은 확인할 수 있습니다.
            </p>

            <details v-if="termViews.length" class="sconf__legal-terms">
              <summary>이용약관 {{ termViews.length }}건</summary>
              <article
                v-for="doc in termViews"
                :key="`${doc.code}-${doc.version}-${doc.idx ?? 'active'}`"
                class="sconf__legal-doc"
              >
                <header class="sconf__legal-head">
                  <div>
                    <strong>{{ doc.title || doc.code }}</strong>
                    <em>
                      이용약관 · {{ doc.code }} · v{{ doc.version }} ·
                      {{ doc.signedAt || doc.signature ? `동의 ${doc.signedAt || '-'}` : '미서명 · 본문 열람' }}
                    </em>
                  </div>
                  <button type="button" class="sui-btn sui-btn--sm" @click="downloadAcceptance(doc)">
                    <AppIcon icon="mdi-download" :size="16" />
                    텍스트 저장
                  </button>
                </header>
                <pre class="sconf__legal-body">{{ doc.body || '(본문 없음)' }}</pre>
              </article>
            </details>
          </template>
        </div>
      </section>
    </div>

    <!-- ============ 우측 요약 ============ -->
    <aside class="sconf__side">
      <section class="sui-card">
        <div class="sui-card__head">
          <h2 class="sui-card__title">업체 요약</h2>
        </div>
        <div class="sui-card__body">
          <dl class="sconf__summary">
            <div><dt>업체 코드</dt><dd>{{ seller?.sellerCode || '-' }}</dd></div>
            <div><dt>가입일</dt><dd>{{ joinedAt }}</dd></div>
            <div><dt>등급</dt><dd>{{ gradeLabel }}</dd></div>
            <div><dt>판매 수수료</dt><dd>{{ seller?.feeRate ?? 0 }}%</dd></div>
            <div><dt>등록 상품</dt><dd>{{ registeredCount }}개</dd></div>
            <div><dt>판매중</dt><dd>{{ sellingCount }}개</dd></div>
          </dl>

          <div class="sconf__actions">
            <button
              type="button"
              class="sui-btn sui-btn--primary sconf__save"
              data-tour="seller-settings-save"
              :disabled="saving"
              @click="onSave"
            >
              {{ saving ? '저장 중...' : '변경 내용 저장' }}
            </button>
            <button type="button" class="sui-btn" @click="onReset">되돌리기</button>
          </div>
        </div>
      </section>

      <section class="sui-card sconf__help">
        <div class="sui-card__body">
          <h3>도움이 필요하신가요?</h3>
          <p>가입 · 정산 · 배송 관련 문의는 운영팀이 평일 09:00~18:00 응대합니다.</p>
          <p class="sconf__help-contact">
            <AppIcon icon="mdi-phone-outline" :size="16" />
            1600-0000
          </p>
          <p class="sconf__help-contact">
            <AppIcon icon="mdi-email-outline" :size="16" />
            seller@hamwoori.kr
          </p>
        </div>
      </section>
    </aside>

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle" :size="18" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import ContractDocumentView from '@/components/seller/ContractDocumentView.vue'
import { downloadMyAcceptancePdf, fetchMyLegalAcceptances } from '@/api/seller'
import { useSellerProducts } from '@/composables/useSellerProducts'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import { MALL_BANKS } from '@/constants/banks'
import { SELLER_GRADE_LABEL, SELLER_STATUS_LABEL } from '@/constants/sellerSession'
import {
  downloadLegalText,
  mergeLegalDocViews,
  type LegalAcceptanceView,
} from '@/utils/legalAcceptancePreview'

const { products: liveProducts, load: loadLiveProducts } = useSellerProducts()
const { seller, isApproved, saveProfile } = useSellerAuth()
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined

const legalViews = ref<LegalAcceptanceView[]>([])
const acceptsLoading = ref(false)
const acceptsError = ref('')
const contractViews = computed(() => legalViews.value.filter((row) => row.kind === 'CONTRACT'))
const termViews = computed(() => legalViews.value.filter((row) => row.kind !== 'CONTRACT'))

async function loadAcceptances() {
  acceptsLoading.value = true
  acceptsError.value = ''
  try {
    const { accepts, activeDocs } = await fetchMyLegalAcceptances()
    legalViews.value = mergeLegalDocViews(accepts, activeDocs)
  } catch (error) {
    legalViews.value = []
    acceptsError.value =
      error instanceof Error ? error.message : '계약·약관 내역을 불러오지 못했습니다.'
  } finally {
    acceptsLoading.value = false
  }
}

function downloadAcceptance(doc: LegalAcceptanceView) {
  downloadLegalText(doc)
}

onMounted(() => {
  void loadLiveProducts()
  void loadAcceptances()
})
const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()

const BANKS = MALL_BANKS

const notifications = [
  { key: 'order' as const, label: '신규 주문', desc: '결제가 완료되면 등록한 이메일과 휴대폰으로 알립니다.' },
  { key: 'claim' as const, label: '취소 · 반품 요청', desc: '요청이 접수되면 등록한 이메일과 휴대폰으로 알립니다.' },
  { key: 'stock' as const, label: '재고 부족', desc: '메일·문자는 보내지 않습니다. 상품 화면에서 확인하세요.' },
  { key: 'inquiry' as const, label: '상품 문의', desc: '메일·문자는 보내지 않습니다. 문의 화면에서 확인하세요.' },
  { key: 'settlement' as const, label: '정산 지급', desc: '메일·문자는 보내지 않습니다. 정산 화면에서 확인하세요.' },
]

const DEFAULT_POLICY =
  '상품 하자 · 오배송은 수령 후 7일 이내 무상 교환 · 반품이 가능합니다.\n단순 변심은 수령 후 7일 이내, 왕복 배송비 부담 조건으로 접수됩니다.\n개봉 · 사용 흔적이 있는 위생용품은 반품이 어렵습니다.'

function initialForm() {
  const current = seller.value
  return {
    storeName: current?.storeName ?? '',
    ownerName: current?.ownerName ?? '',
    managerName: current?.managerName ?? '',
    bizName: current?.bizName ?? '',
    bizNumber: current?.bizNumber ?? '',
    phone: current?.phone ?? '',
    email: current?.email ?? '',
    zipcode: current?.zipcode ?? '',
    address: current?.address ?? '',
    addressDetail: current?.addressDetail ?? '',
    settleBank: current?.settleBank ?? '',
    settleAccount: current?.settleAccount ?? '',
    settleHolder: current?.settleHolder ?? '',
    shippingFee: current?.shippingFee ?? 3000,
    freeShipOver: 0,
    returnFee: current?.returnFee ?? 3000,
    cutoff: current?.cutoff || '14:00',
    returnPolicy: current?.returnPolicy || DEFAULT_POLICY,
    notify: {
      order: current?.notify?.order ?? true,
      claim: current?.notify?.claim ?? true,
      stock: current?.notify?.stock ?? true,
      inquiry: current?.notify?.inquiry ?? true,
      settlement: current?.notify?.settlement ?? false,
    },
  }
}

const form = ref(initialForm())
const saving = ref(false)

const registeredCount = computed(() => liveProducts.value.length)
const sellingCount = computed(
  () => liveProducts.value.filter((item) => item.status === 'SELLING').length,
)

function notify(message: string) {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 2400)
}
const statusLabel = computed(() =>
  seller.value ? SELLER_STATUS_LABEL[seller.value.status] : '확인 중',
)
const gradeLabel = computed(() => SELLER_GRADE_LABEL[seller.value?.grade ?? 'NORMAL'] ?? '일반')
const joinedAt = computed(() => (seller.value?.joinedAt ?? '').slice(0, 10) || '-')

async function onSave() {
  if (saving.value) {
    return
  }
  if (!window.confirm('변경 내용을 저장할까요?')) return

  saving.value = true

  try {
    await saveProfile({
      storeName: form.value.storeName,
      bizName: form.value.bizName,
      ownerName: form.value.ownerName,
      managerName: form.value.managerName,
      email: form.value.email,
      phone: form.value.phone,
      zipcode: form.value.zipcode,
      address: form.value.address,
      addressDetail: form.value.addressDetail,
      settleBank: form.value.settleBank,
      settleAccount: form.value.settleAccount,
      settleHolder: form.value.settleHolder,
      shippingFee: form.value.shippingFee,
      freeShipOver: 0,
      returnFee: form.value.returnFee,
      cutoff: form.value.cutoff,
      returnPolicy: form.value.returnPolicy,
      notify: { ...form.value.notify },
    })
    form.value = initialForm()
    notify('판매업체 정보를 저장했습니다.')
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '판매업체 정보 저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

async function onSearchAddress() {
  try {
    const picked = await searchPostcode()
    if (!picked) {
      return
    }
    form.value.zipcode = picked.zipcode
    form.value.address = picked.extra ? `${picked.address} ${picked.extra}` : picked.address
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '주소 검색을 열지 못했습니다.')
  }
}

function onReset() {
  if (!window.confirm('작성 중인 변경 내용을 되돌릴까요?')) return
  form.value = initialForm()
  notify('변경 내용을 되돌렸습니다.')
}
</script>

<style lang="scss" src="./SellerSettingsPage.scss"></style>
